import { typographyTokens } from '../../theme/typographyTokens';
import React, { useState, useMemo, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Autocomplete, {
  AutocompleteCloseReason,
  autocompleteClasses,
} from '@mui/material/Autocomplete';
import Stack from '@mui/material/Stack';
import InputBase from '@mui/material/InputBase';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { Divider, InputAdornment, useTheme } from '@mui/material';

interface PopperComponentProps {
  anchorEl?: any;
  disablePortal?: boolean;
  open: boolean;
}

const StyledAutocompletePopper = styled('div')(({ theme }) => ({
  [`& .${autocompleteClasses.paper}`]: {
    boxShadow: 'none',
    margin: 0,
    color: 'inherit',
    fontSize: typographyTokens.fontSizes.sm,
    width: '300px',
  },
  [`& .${autocompleteClasses.listbox}`]: {
    backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1c2128',
    padding: 0,
    [`& .${autocompleteClasses.option}`]: {
      minHeight: 'auto',
      alignItems: 'flex-start',
      padding: 8,
      borderBottom: `1px solid  ${
        theme.palette.mode === 'light' ? ' #eaecef' : '#30363d'
      }`,
      '&[aria-selected="true"]': {
        backgroundColor: 'transparent',
      },
      [`&.${autocompleteClasses.focused}, &.${autocompleteClasses.focused}[aria-selected="true"]`]:
        {
          backgroundColor: theme.palette.action.hover,
        },
    },
  },
  [`&.${autocompleteClasses.popperDisablePortal}`]: {
    position: 'relative',
  },
}));

function PopperComponent(props: PopperComponentProps) {
  const { disablePortal, anchorEl, open, ...other } = props;
  return <StyledAutocompletePopper {...other} />;
}

const StyledPopper = styled(Popper)(({ theme }) => ({
  border: `1px solid ${theme.palette.mode === 'light' ? '#e1e4e8' : '#30363d'}`,
  boxShadow: `0 8px 24px ${
    theme.palette.mode === 'light' ? 'rgba(149, 157, 165, 0.2)' : 'rgb(1, 4, 9)'
  }`,
  borderRadius: 6,
  width: '300px',
  zIndex: theme.zIndex.modal,
  fontSize: typographyTokens.fontSizes.sm,
  color: theme.palette.mode === 'light' ? '#24292e' : '#c9d1d9',
  backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1c2128',
}));

const StyledInput = styled(InputBase)(({ theme }) => ({
  padding: 3,
  width: '270px',
  borderRadius: 4,
  outline: 'none',
  border: '1px solid #7E8083',
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#FBFCFC',
  '& input': {
    padding: '4px',
  },
  fontSize: typographyTokens.fontSizes.body,
  '&:focus': {
    outline: 'none',
  },
}));

export default function AutoComplete({
  type,
  show,
  adAppRoleName,
  anchor,
  showConfirmButton,
  data,
  onClose,
  onChange,
  helperText,
}: {
  type: string;
  show: boolean;
  adAppRoleName?: string;
  anchor: null | HTMLElement;
  showConfirmButton?: boolean;
  data: Array<any>;
  onClose: () => void;
  onChange: (changedValue: any) => void;
  helperText?: string;
}) {
  const [pendingValue, setPendingValue] = useState(undefined);
  const [inputValue, setInputValue] = useState('');
  const [debouncedInput, setDebouncedInput] = useState('');
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  // Debounce input for filtering
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedInput(inputValue.trim().toLowerCase());
    }, 300);
    return () => clearTimeout(handler);
  }, [inputValue]);

  // Efficient, memoized filtering
  const filteredRows = useMemo(() => {
    if (!data) return [];
    if (!debouncedInput || debouncedInput.length < 3) return data;
    return data.filter((item) =>
      Object.values(item).some((value) =>
        String(value).toLowerCase().includes(debouncedInput)
      )
    );
  }, [data, debouncedInput]);

  const handleChange = (e: any) => {
    if (e === undefined) return;
    onChange(e);
  };

  const handleClose = () => {
    setInputValue('');
    setPendingValue(undefined);
    onClose();
  };

  return (
    <StyledPopper open={show} anchorEl={anchor} placement="bottom-start">
      <ClickAwayListener onClickAway={handleClose}>
        <div>
          <Autocomplete
            open
            onClose={(
              event: React.ChangeEvent<{}>,
              reason: AutocompleteCloseReason
            ) => {
              if (reason === 'escape') {
                handleClose();
              }
            }}
            inputValue={inputValue}
            onInputChange={(event, value, reason) => {
              setInputValue(value);
            }}
            onChange={(event, newValue, reason) => {
              setPendingValue(newValue ? newValue : undefined);
            }}
            disableCloseOnSelect
            PopperComponent={PopperComponent}
            renderTags={() => null}
            noOptionsText="No result found."
            renderOption={(props, option, { selected }) => (
              <li {...props} style={{ padding: 0 }}>
                <Box
                  onClick={() =>
                    showConfirmButton || showConfirmButton === undefined
                      ? null
                      : handleChange(option)
                  }
                  sx={{
                    flexGrow: 1,
                    padding: '8px',
                    backgroundColor: selected ? (isDarkMode ? theme.palette.background.default : '#F3F8FC') : (isDarkMode ? theme.palette.background.default : '#fff'),
                    color: selected  ? (isDarkMode ? theme.palette.primary.main : '#2372B9') : (isDarkMode ? '#FFFFFF' : '#222'),
                    '&:hover': {
                      color: '#2372B9',
                    },
                    '&:hover > span': {
                      color: '#646669',
                    },
                    '& span': {
                      color: '#646669',
                    },
                  }}
                >
                  {option?.name || (option?.subject && `Id: ${option?.id} - ${option?.subject}`)}
                </Box>
              </li>
            )}
            options={filteredRows}
            filterOptions={(options) => options}
            getOptionLabel={(option) => option.displayName || option.name || ''}
            renderInput={(params) => (
              <>
                <Box sx={{ padding: '8px 16px', backgroundColor: isDarkMode ? theme.palette.background.default : '#E5E9EA', display: 'flex', alignItems: 'center' }}>
                  <StyledInput
                    size="small"
                    ref={params.InputProps.ref}
                    inputProps={params.inputProps}
                    startAdornment={
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    }
                    autoFocus
                    placeholder={'Search (min 3 characters)'}
                  />
                  {inputValue && (
                    <CloseIcon
                      style={{ cursor: 'pointer', marginLeft: '8px' }}
                      onClick={() => setInputValue('')}
                    />
                  )}
                </Box>
                <Divider />
              </>
            )}
          />
          {(showConfirmButton || showConfirmButton === undefined) && (
            <>
              <Divider />
              <Stack
                direction="row"
                justifyContent="right"
                spacing={2}
                sx={{
                  p: 1,
                }}
              >
                <Button
                  size="small"
                  variant="text"
                  onClick={handleClose}
                  sx={{ textTransform: 'none' }}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleChange(pendingValue)}
                  sx={{ textTransform: 'none' }}
                >
                  {type === 'assigner'
                    ? 'Save Assignee'
                    : 'Save Resource Owner'}
                </Button>
              </Stack>
            </>
          )}
        </div>
      </ClickAwayListener>
    </StyledPopper>
  );
}
